import { BestSeller, BettingApi } from 'api';
import { ActionType } from 'api/betslipAnalytics.api';
import PrematchTracker from 'features/eventPreMatch/component/preMatchTracker';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LiveEventLoader } from 'shared/components/loader';
import { APP_URL, COMBINED_BET_MARKET_TYPE, TRACKER_TABS } from 'shared/constants/common';
import {
	checkMeta,
	eventPageDescription,
	eventPageTitle,
	getParticipants,
	getSelectedMarketTab,
	isEsport,
	removeSpecialCharacter,
	setAllBets,
	sportParamsId,
} from 'shared/util/utility';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import EventStatus from 'shared/components/EventStatus';
import { HelmetMetaTags } from 'shared/components/HelmetMetaTags';
import { addNotification } from 'shared/components/toast';
import { useQuickShotsEnabledFlag } from 'shared/featureFlags';
import { useBetslipAnalytics } from 'shared/hooks/useBetslipAnalytics';
import { BETSType, CouponState, State } from 'shared/interface';
import { addBet } from 'shared/store/coupon.action';
import { setEventPopup } from 'shared/store/navigation.action';
import 'swiper/css';
import { gameTypeIsBetBuilder } from '../../../shared/components/outcomeCards/gameTypeIds';
import { DescriptionObj, MarketTypesState, TitleObj } from '../../eventLive/interface/interface';
import BetBuilderDialog from '../component/BetBuilderDialog';
import PreMatchBetOptions from '../component/preMatchBetOptions';
import QuickShotsSwiper from '../component/QuickShot/quickShotsSwiper';
import RatingBar from '../component/QuickShot/RatingBar';
import { Betbuilder, BetbuilderOutcome, EventAllDetailsDataState, EventState, GamesState } from '../interface/sportPrematch';
import { fetchEventDetailData, resetEventDetailData } from '../store/sportPrematch.action';

interface PropsParam {
	id: string;
	sportName: string;
	parentName: string;
	categoryName: string;
}

const eventAllDetailInitial = {
	backgroundURL: '',
	customeBets: [],
	event: {
		categoryId: 0,
		categoryName: '',
		eventCodeId: 0,
		eventExtendedData: { remoteCategoryId: '' },
		eventId: 0,
		eventName: '',
		eventStart: 0,
		eventType: 0,
		games: [],
		gamesCount: 0,
		isLive: false,
		marketTypes: [],
		parentId: 0,
		parentName: '',
		participants: [],
		remoteId: 0,
		sportId: 0,
		sportName: '',
		treatAsSport: 0,
		statScore: 0,
		disableNoFollow: false,
	},
};

const eventDetailInitial = {
	categoryId: 0,
	categoryName: '',
	eventCodeId: 0,
	eventExtendedData: { remoteCategoryId: '' },
	eventId: 0,
	eventName: '',
	eventStart: 0,
	eventType: 0,
	games: [],
	gamesCount: 0,
	isLive: false,
	marketTypes: [],
	parentId: 0,
	parentName: '',
	participants: [],
	remoteId: 0,
	sportId: 0,
	sportName: '',
	treatAsSport: 0,
	statScore: 0,
	disableNoFollow: false,
	backgroundURL: '',
};

const titleInitial = {
	eventName: '',
	eventSport: '',
};

const descriptionInitial = {
	categoryName: '',
	eventName: '',
	eventSport: '',
};

export const PreMatch = () => {
	const { addAction } = useBetslipAnalytics();
	const location = useLocation();
	const dispatch = useDispatch();
	const trackerPrematchRef = useRef<HTMLDivElement>(null);
	const [hideRatingBar, setHideRatingBar] = useState(false);
	const [activeTrackerTab, setActiveTrackerTab] = useState('All');
	const [adjacentEvents, setAdjacentEvents] = useState({ next: '', previous: '' });
	const [isLoading, setIsLoading] = useState(false);
	const [titleObj, setTitleObj] = useState<TitleObj>(titleInitial);
	const [descriptionObj, setDescriptionObj] = useState<DescriptionObj>(descriptionInitial);
	const [eventDetailData, setEventDetailData] = useState<EventState>(eventDetailInitial);
	const [eventAllDetails, setAllDetails] = useState<EventAllDetailsDataState>(eventAllDetailInitial);
	const [customeBets, setCustomeBets] = useState<number[]>([]);
	const [marketTypes, setMarketTypes] = useState<string[]>([]);
	const [currentMarketType, setCurrentMarketType] = useState<MarketTypesState[]>([]);
	const [selectedOddsArray, setSelectedOddsArray] = useState<GamesState[]>([]);
	const [totalOdds, setTotalOdds] = useState<number>(0);
	const [sameLeagueEvents, setSameLeagueEvents] = useState([]);
	const [conflictedIds, setConflictedIds] = useState<number[]>([]);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
	const [activePillTab, setActivePillTab] = useState('All');
	const params = useParams<PropsParam>();
	const { sportName, id } = params;
	const sportIdFromSportName = sportParamsId.get(sportName) as number;
	const analytics = useSelector((state: State) => state.coupon.analytics);
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const [filteredList, setFilteredList] = useState<GamesState[]>([]);
	const eventNotFound = !!(eventDetailData.eventExtendedData?.liveEventId || eventDetailData.result);
	const hasBetBuilderBets = customeBets.length > 0;
	const hasSameLeagueEvents = sameLeagueEvents.length > 1;
	const quickShotRatingSubmitted = localStorage.getItem('quickShotRatingSubmitted');

	const isQuickShots = useQuickShotsEnabledFlag();
	const [filteredGames, setFilteredGames] = useState({});

	const filterByMarketType = (marketType: number, filteredGames: any) => {
		const toNumber = Number(marketType);
		const newFilteredGames = Object.keys(filteredGames).reduce((acc: any, key: any) => {
			const filteredItems = filteredGames[key].filter((game: any) => {
				return game.marketTypes.includes(toNumber);
			});
			if (filteredItems.length > 0) {
				acc[key] = filteredItems;
			}
			return acc;
		}, {});
		setFilteredGames(newFilteredGames);
	};

	const handleEventPopup = useCallback(
		(dataMapProp: any) => {
			if (dataMapProp.size > 0) {
				dispatch(setEventPopup(true));
			} else {
				dispatch(setEventPopup(false));
			}
		},
		[dispatch],
	);

	const fetchPreMatchEventDetail = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await BestSeller.prematchEventDetail(parseInt(id), sportIdFromSportName);
			const responseData = response.data.data;
			const responseDataEvent = response.data.data.event;
			const eventDetailsResponse = await BestSeller.eventDetailsById(responseDataEvent.categoryId);

			const { categoryName, eventName, sportName } = responseDataEvent;
			setTitleObj({ eventName: eventName, eventSport: sportName });
			setDescriptionObj({ categoryName, eventName, eventSport: sportName });
			let marketTypes = ['All'];
			if (responseData.customeBets.length) {
				marketTypes.push('Builder');
			}

			marketTypes.push(...responseDataEvent.marketTypes.map((m: MarketTypesState) => String(m.marketType)));
			setEventDetailData(() => responseData.event);
			setAllDetails(responseData);
			setActivePillTab(getSelectedMarketTab(activePillTab, marketTypes));
			marketTypes = filterQuickShotMarketType(responseDataEvent.marketTypes, marketTypes, responseData.event);
			setMarketTypes(marketTypes);
			setCurrentMarketType(responseDataEvent.marketTypes);
			setCustomeBets(responseData.customeBets);
			if (eventDetailsResponse) {
				setSameLeagueEvents(eventDetailsResponse.data.data.events);
			}
		} catch (error) {
			console.log('err', error);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, handleEventPopup]);

	const filterQuickShotMarketType = (marketTypes: MarketTypesState[], marketTypesArray: string[], eventDetailData: EventState) => {
		marketTypesArray = marketTypesArray.filter((marketType) => marketType !== String(COMBINED_BET_MARKET_TYPE));
		const quickShotMarketType = marketTypes.find((marketType) => marketType.marketType === COMBINED_BET_MARKET_TYPE);
		if (quickShotMarketType) {
			const gameIds = quickShotMarketType.gameIds.filter((game) => {
				const eventDetails = eventDetailData.games.find((item: any) => item.gameId === game);
				return !eventDetails?.gameType || !gameTypeIsBetBuilder(eventDetails?.gameType);
			});
			if (gameIds.length > 0) {
				marketTypesArray.push(String(COMBINED_BET_MARKET_TYPE));
			}
		}
		return marketTypesArray;
	};

	const changeCurrentTabMainState = (activePillTab: string, currentMarketType: MarketTypesState[]) => {
		setCurrentMarketType(currentMarketType);
		setActivePillTab(activePillTab);
	};

	const findNextAndPreviousEvents = (events: any, params: any) => {
		const currentIndex = events.findIndex((event: any) => event.eventId === parseInt(params.id));
		if (currentIndex !== -1) {
			const nextIndex = currentIndex + 1;
			const previousIndex = currentIndex - 1;
			const nextEvent = nextIndex < events.length ? events[nextIndex] : events[0];
			const previousEvent = previousIndex >= 0 ? events[previousIndex] : events[events.length - 1];
			const baseRoute = isEsport(events[0]) ? APP_URL.eSport : APP_URL.sport;
			const nextParticipant = getParticipants(nextEvent);
			const prevParticipant = getParticipants(previousEvent);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			let finalNextParticipant = removeSpecialCharacter(nextParticipant[0]!);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			let finalPreviousParticipant = removeSpecialCharacter(prevParticipant[0]!);
			if (nextParticipant[1]) {
				finalNextParticipant += `-${removeSpecialCharacter(nextParticipant[1])}`;
			}
			if (prevParticipant[1]) {
				finalPreviousParticipant += `-${removeSpecialCharacter(prevParticipant[1])}`;
			}
			setAdjacentEvents({
				next: `${baseRoute}/${params.sportName}/${params.parentName}/${params.categoryName}/${finalNextParticipant}/${nextEvent.eventId}`,
				previous: `${baseRoute}/${params.sportName}/${params.parentName}/${params.categoryName}/${finalPreviousParticipant}/${previousEvent.eventId}`,
			});
		}
	};

	const setBetBuilderArray = useCallback(
		(selectedOddsArray: GamesState[]) => {
			setSelectedOddsArray(selectedOddsArray);
		},
		[setSelectedOddsArray],
	);

	const pageTitle = checkMeta(titleObj, () => eventPageTitle(titleObj, false));
	const metaDescription = checkMeta(descriptionObj, () => eventPageDescription(descriptionObj));

	const { t } = useTranslation();

	const getOdds = useCallback(
		async (selectedOddsArrayParam: GamesState[], updateState = false) => {
			if (selectedOddsArrayParam.length <= 1) {
				setSelectedOddsArray(selectedOddsArrayParam);
				setBetBuilderArray(selectedOddsArrayParam);
				setTotalOdds(0);
				return;
			}
			const response = await BettingApi.validationOdds(selectedOddsArrayParam.map((item) => item.outcomeId));
			if (response.data.code === 400) {
				addNotification('info', t('betBuilderMaxCouponsLimit'));
			}
			if (response.data.data.validationResult.odds !== -1) {
				const conflictedOutcomeIds: number[] = [];
				response.data.data.validationResult.availableGames.forEach((outcomes: Betbuilder) => {
					outcomes.outcomes.forEach((outcome: BetbuilderOutcome) => {
						if (outcome.conflict) {
							conflictedOutcomeIds.push(outcome.outcomeId);
						}
					});
				});
				setConflictedIds(conflictedOutcomeIds);
			}

			if (response.data.data.validationResult.odds === -1) {
				addNotification('warning', t('betBuilderExclusiveOddsWarning'));
			} else {
				setSelectedOddsArray(selectedOddsArrayParam);
				setTotalOdds(response.data.data.validationResult.odds);
				setBetBuilderArray(selectedOddsArrayParam);
			}
		},
		[setBetBuilderArray, t],
	);

	const clickOnCross = () => {
		setSelectedOddsArray([]);
		setTotalOdds(0);
		setBetBuilderArray([]);
		setConflictedIds([]);
	};

	const onClickRemoveGame = (item: number) => {
		const index = selectedOddsArray.findIndex((game) => game.outcomeId === item);
		const restArray = [...selectedOddsArray];
		restArray.splice(index, 1);
		setConflictedIds([]);
		getOdds(restArray, true);
	};

	const updateCoupon = async (selectedOddsArray: GamesState[], bets: BETSType, eventDetailData: EventState) => {
		if (totalOdds <= 0) {
			return;
		}

		const response = await BettingApi.calculateOdds(selectedOddsArray.map((item) => item.outcomeId));
		const outcomes = response?.data?.data?.outcome || {};

		const betBuilderOddsArray = outcomes.gameName.split('|').map((name: any, index: any) => {
			return {
				gameName: name,
				name: outcomes.name.split('|')[index],
			};
		});

		const existingCouponIndex = bets.outcomes.findIndex(
			(coupon: any) => coupon.eventId === outcomes.eventId && selectedOddsArray.every((selectedBet: GamesState) => selectedBet.outcomeId),
		);

		if (existingCouponIndex >= 0) {
			bets.outcomes.splice(existingCouponIndex, 1);
		}

		const betObj = {
			outcomeId: outcomes.outcomeId,
			outcomeOdds: outcomes.odds,
			outcomeName: outcomes.name,
			eventId: outcomes.eventId,
			eventStart: eventDetailData.eventStart,
			eventType: 1,
			gameName: outcomes.gameName,
			gameId: outcomes.gameId,
			eventName: eventDetailData.eventName,
			league: `${eventDetailData.sportName} / ${eventDetailData.parentName} / ${eventDetailData.categoryName}`,
			selectedBet: true,
			sportId: eventDetailData.sportId,
			banker: false,
			isLive: false,
			outcomeLive: false,
			isBetBuilder: true,
			betBuilderOddsArray: betBuilderOddsArray,
		};
		const isInBetslip = bets.outcomes.some((item) => item.eventId === outcomes.eventId);
		addAction(betObj, location.pathname, isInBetslip ? ActionType.REMOVE : ActionType.ADD);
		dispatch(addBet({ ...bets, ...setAllBets(bets, betObj, dispatch, coupon, analytics, t) }));
		clickOnCross();
		setConflictedIds([]);
	};

	const onAddBetToCoupon = async () => {
		if (totalOdds <= 0) {
			return;
		}

		const response = await BettingApi.calculateOdds(selectedOddsArray.map((item) => item.outcomeId));
		const outcomes = response?.data?.data?.outcome || {};

		if (bets.outcomes.some((item) => item.eventId === outcomes.eventId)) {
			setOpenDialog(true);
		} else {
			await updateCoupon(selectedOddsArray, bets, eventDetailData);
		}
	};

	const handleDialogConfirm = async () => {
		setOpenDialog(false);
		await updateCoupon(selectedOddsArray, bets, eventDetailData);
	};

	useEffect(() => {
		fetchPreMatchEventDetail();
	}, [fetchPreMatchEventDetail]);

	useEffect(() => {
		dispatch(fetchEventDetailData(eventAllDetails));
	}, [dispatch, eventAllDetails]);

	useEffect(() => {
		return () => {
			dispatch(resetEventDetailData());
		};
	}, [dispatch]);

	const quickShotMarketType = useMemo(() => {
		const quickShotMarket = currentMarketType.find((marketType) => marketType.marketType === COMBINED_BET_MARKET_TYPE);
		if (!quickShotMarket) return {} as MarketTypesState;

		const gameIds = quickShotMarket.gameIds.filter((game) => {
			const eventDetails = eventDetailData.games.find((item: any) => item.gameId === game);
			return eventDetails?.gameType && gameTypeIsBetBuilder(eventDetails?.gameType);
		});

		return gameIds.length > 0 ? { marketType: quickShotMarket.marketType, gameIds } : ({} as MarketTypesState);
	}, [currentMarketType]);

	const notQuickShotMarketType = useMemo(() => {
		const notQuickShotMarkets = currentMarketType.filter((marketType) => marketType.marketType !== COMBINED_BET_MARKET_TYPE);

		const quickShotMarket = currentMarketType.find((marketType) => marketType.marketType === COMBINED_BET_MARKET_TYPE);
		if (!quickShotMarket) return notQuickShotMarkets;

		const gameIds = quickShotMarket.gameIds.filter((game) => {
			const eventDetails = eventDetailData.games.find((item: any) => item.gameId === game);
			return !eventDetails?.gameType || !gameTypeIsBetBuilder(eventDetails?.gameType);
		});

		if (gameIds.length > 0) {
			notQuickShotMarkets.push({ marketType: quickShotMarket.marketType, gameIds });
		}

		return notQuickShotMarkets;
	}, [currentMarketType]);

	const isDetailDataInitial = isEqual(eventDetailData, eventDetailInitial);
	return (
		<>
			<HelmetMetaTags title={pageTitle} description={metaDescription} is404={eventNotFound} />
			<div className="pre-match-wrapper sport-prematch">
				{!isLoading ? (
					<>
						{isDetailDataInitial ? (
							<EventStatus link={APP_URL.live} title={t('eventStarted')} buttonText={t('returnToLive')} />
						) : (
							<>
								<PrematchTracker
									filteredGames={filteredGames}
									hasSameLeagueEvents={hasSameLeagueEvents}
									hasBetBuilderBets={hasBetBuilderBets}
									sameLeagueEvents={sameLeagueEvents}
									adjacentEvents={adjacentEvents}
									findNextAndPreviousEvents={findNextAndPreviousEvents}
									setActivePillTab={setActivePillTab}
									activeTrackerTab={activeTrackerTab}
									setActiveTrackerTab={setActiveTrackerTab}
									eventDetailData={eventDetailData}
									marketTypes={marketTypes}
									changeCurrentTabMainState={changeCurrentTabMainState}
									trackerPrematchRef={trackerPrematchRef}
									selectedOddsArray={selectedOddsArray}
									onClickRemoveGame={onClickRemoveGame}
									clickOnCross={clickOnCross}
									onAddBetToCoupon={onAddBetToCoupon}
									totalOdds={totalOdds}
									setTotalOdds={setTotalOdds}
									activePillTab={activePillTab}
									setFilteredList={setFilteredList}
									filterByMarketType={filterByMarketType}
									setFilteredGames={setFilteredGames}
								/>
								{isQuickShots?.value &&
									activePillTab !== TRACKER_TABS.BET_BUILDER &&
									activePillTab !== String(COMBINED_BET_MARKET_TYPE) &&
									quickShotMarketType &&
									quickShotMarketType?.gameIds?.length > 0 && (
										<div className="mb-6">
											<QuickShotsSwiper
												eventDetailData={eventDetailData}
												quickShotMarketType={quickShotMarketType}
												isLive={false}
											/>
											{!quickShotRatingSubmitted && !hideRatingBar && (
												<RatingBar hideRatingBar={() => setHideRatingBar(true)} />
											)}
										</div>
									)}
								<PreMatchBetOptions
									filteredGames={filteredGames}
									eventDetailData={eventDetailData}
									currentMarketType={notQuickShotMarketType}
									customeBets={customeBets}
									setBetBuilderArray={setBetBuilderArray}
									fetchPreMatchEventDetail={fetchPreMatchEventDetail}
									selectedOddsArray={selectedOddsArray}
									setSelectedOddsArray={setSelectedOddsArray}
									totalOdds={totalOdds}
									setTotalOdds={setTotalOdds}
									activePillTab={activePillTab}
									filteredList={filteredList}
									activeTrackerTab={activeTrackerTab}
									conflictedIds={conflictedIds}
									setConflictedIds={setConflictedIds}
								/>
							</>
						)}
					</>
				) : (
					<LiveEventLoader />
				)}
				<BetBuilderDialog
					openDialog={openDialog}
					setDialogOpen={setOpenDialog}
					onAddBetToCoupon={handleDialogConfirm}
					clickOnCross={clickOnCross}
				/>
			</div>
		</>
	);
};
